import { useCallback, useContext, useEffect, useRef, useState } from "react";
import ProjectSection from "./ProjectSection";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import LoadingCircle from "../utility/LoadingCircle";
import StagePrompt from "./StagePrompt";
import segmentImage from "../../assets/segmentimage.png";
import FileSaver from "file-saver";
import { BsArrowLeftRight, BsArrowUpCircleFill } from "react-icons/bs";
import { AiOutlineExport, AiFillWarning } from "react-icons/ai";
import { FaMapMarkerAlt } from "react-icons/fa";
import { ImArrowLeft } from "react-icons/im";
import { ImArrowRight } from "react-icons/im";
import ExportModal from "./ExportModal";
import ReportButton from "./buttons/ReportButton";
import UpdateTemplateButton from "./buttons/UpdateTemplateButton";
import ScrollToTop from "react-scroll-to-top";
import NavigationArea from "./NavigationArea";
import ClearRecordButton from "./buttons/ClearRecordButton";
import MessageLogs from "./Messages/MessageLogs";
import { IoChatbubbleEllipsesSharp } from "react-icons/io5";
import { FaDropbox } from "react-icons/fa";
import ClockOutButton from "../Clocking/ClockOutButton";
import ClockInButton from "../Clocking/ClockInButton";
import NearingLateClockoutBanner from "../Clocking/NearingLateClockoutBanner";
import LateClockOutBanner from "../Clocking/LateClockoutBanner";
import ShopButton from "../shop/ShopButton";
import UserContext from "../User/UserContext";
import PermissionGuard from "../User/PermissionGuard";
import PermissionsMap from "../User/PermissionsMap";
import GeneratePublicLinkButton from "../public/GeneratePublicLinkButton";
import ApproveDesignButton from "../public/ApproveDesignButton";
import RejectDesignButton from "../public/RejectDesignButton";
import SolarGradeUpdateButton from "./buttons/SolarGradeUpdateButton";
import OldTemplateBanner from "./OldTemplateBanner";
import { RoomsSearchView } from "@1stle/messenger-library";
import AerialView from "./aerial/AerialView";

const Project = ({
	isGuest = false,
	guestToken = "",
	isApproved = false,
	isRejected = false,
	rejectedBy = "",
	rejectedReason = "",
	rejectedAt = null,
	shopEnabled = 0,
}) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const ppc = searchParams.get("ppc");
	const params = useParams();
	const DELETE_ID = 1; // hardcoded odoo user id that allows deleting 2 for user admin 1 for employee admin
	const [sections, setSections] = useState([]);
	const [projectName, setProjectName] = useState("");
	const [odooId, setOdooId] = useState();
	const [templateName, setTemplateName] = useState("");
	const [templateId, setTemplateId] = useState("");
	const [templateVersion, setTemplateVersion] = useState("");
	const [conditionalData, setConditionalData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [stage, setStage] = useState("");
	const [showStagePrompt, setShowStagePrompt] = useState(false);
	const [templateTypeId, setTemplateTypeId] = useState(-1);
	const [showExportMenu, setShowExportMenu] = useState(false);
	const [googleMapLink, setGoogleMapLink] = useState("");
	const [requiredFieldsBySection, setRequiredFieldsBySection] = useState({});
	const [aerialMarkersMissingBySection, setAerialMarkersMissingBySection] = useState({});
	const [allInitialThumbnailPathes, setAllInitialThumbnailPathes] = useState([]);
	const [lastUpdatedBy, setLastUpdatedBy] = useState("");
	const [lastModifiedOn, setLastModifiedOn] = useState("");
	const [updatingTemplateVersion, setUpdatingTemplateVersion] = useState(false);
	const [templateStatus, setTemplateStatus] = useState(-1);
	const [showElement, setShowElement] = useState(false);
	const [selectedOdooModel, setSelectedOdooModel] = useState("");
	const [templateNavMappings, setTemplateNavMappings] = useState("");
	const [contractType, setContractType] = useState("all");
	const [portfolioType, setPortfolioType] = useState("all");
	const [parentRecord, setParentRecord] = useState(false);
	const [childRecords, setChildRecords] = useState([]);
	const [tickets, setTickets] = useState([]);
	const [odooInstallationName, setOdooInstallationName] = useState("");
	const [odooParentName, setOdooParentName] = useState("");
	const [helpdeskStage, setHelpdeskStage] = useState("");
	const [installationRecord, setInstallationRecord] = useState([]);
	const [openChatLog, setOpenChatLog] = useState(false);
	const [movingToSIQ, setMovingToSIQ] = useState(false);
	const [dropboxLinkPath, setDropboxLinkPath] = useState("");
	const [showInvalidDropboxModal, setShowInvalidDropboxModal] = useState(false);
	const [clockedIntoThis, setClockedIntoThis] = useState(false);
	const [clockingData, setClockingData] = useState({});
	const [clockBannerToShow, setClockBannerToShow] = useState("");
	const [hasPreProjectCompleteTicket, setHasPreProjectCompleteTicket] = useState(false);
	const [projectLocation, setProjectLocation] = useState(null);
	const [hasGeoFencing, setHasGeoFencing] = useState(true);
	const [publicEnabledTemplate, setPublicEnabledTemplate] = useState(false);
	const [keyedSolarGradeData, setKeyedSolarGradeData] = useState({});
	const [solarGradeIds, setSolarGradeIds] = useState({});
	const [keyedKrummySolarGradeData, setKeyedKrummySolarGradeData] = useState({});
	const [solarGradeFilteredSections, setSolarGradeFilteredSections] = useState([]);
	const [startingPendingImages, setStartingPendingImages] = useState([]);
	const [activeTab, setActiveTab] = useState("project"); // "project" or "chat"
	const [aerialData, setAerialData] = useState(null);
	const [selectedAerialDropboxId, setSelectedAerialDropboxId] = useState(null);
	const [aerialLoading, setAerialLoading] = useState(false);
	const [aerialLoadError, setAerialLoadError] = useState("");
	const [focusedFieldId, setFocusedFieldId] = useState(null);
	const [aerialFocusRequestId, setAerialFocusRequestId] = useState(0);
	const [aerialSyncEpoch, setAerialSyncEpoch] = useState(0);
	const aerialFieldOverridesReference = useRef({});
	const { userPermissions = [] } = useContext(UserContext);

	let { user, api, token } = useContext(UserContext);
	const [localIsRejected, setLocalIsRejected] = useState(isRejected);
	const [localRejectedBy, setLocalRejectedBy] = useState(rejectedBy);
	const [localRejectedReason, setLocalRejectedReason] = useState(rejectedReason);
	const [localRejectedAt, setLocalRejectedAt] = useState(rejectedAt);
	const [showRejectNote, setShowRejectNote] = useState(false);

	useEffect(() => {
		setLocalIsRejected(isRejected);
		setLocalRejectedBy(rejectedBy);
		setLocalRejectedReason(rejectedReason);
		setLocalRejectedAt(rejectedAt);
	}, [isRejected, rejectedBy, rejectedReason, rejectedAt]);

	let projectId = params.id;
	if (isGuest) {
		api = api + "/public";
		token = guestToken;
	}

	const formatRejectedAt = (value) => {
		if (!value) {
			return "";
		}
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
	};

	const validateReject = async () => {
		const targetFieldName = "Customer Proposed Design Changes";
		const normalizedTarget = targetFieldName.trim().toLowerCase();

		for (const section of sections) {
			const response = await fetch(api + "/project/section/" + section.id + "?project_id=" + projectId, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + token,
				},
			});

			if (!response.ok) {
				return {
					allow: false,
					message: "We could not verify required fields. Please try again.",
				};
			}

			const data = await response.json();
			const fields = Array.isArray(data?.fields) ? data.fields : [];
			const fieldMatch = fields.find((field) => (field?.name || "").trim().toLowerCase() === normalizedTarget);

			if (!fieldMatch) {
				continue;
			}

			const fieldData = Array.isArray(data?.projectData) ? data.projectData : [];
			const matchingData = fieldData.find((entry) => String(entry?.field_id) === String(fieldMatch.id));
			const comment = (matchingData?.comment || "").toString().trim();

			if (!comment) {
				return {
					allow: false,
					message: 'Please complete the "Customer Proposed Design Changes" comment before rejecting.',
				};
			}

			return { allow: true };
		}

		return { allow: true };
	};

	useEffect(() => {
		setTimeout(function () {
			setShowElement(true);
		}, 5000);
	}, []);

	const deleteThisProject = (e) => {
		e.preventDefault();
		fetch(api + "/project/" + projectId, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
		}).then((res) => {
			if (res.ok) {
				navigate("/dashboard");
			}
		});
	};

	useEffect(() => {
		let foundId = false;
		let clockingInfo = clockingData.clockingInfo;
		if (!clockingInfo) {
			return;
		}
		if (selectedOdooModel === "project.task") {
			if (clockingInfo.installationIds && clockingInfo.installationIds.includes(odooId)) {
				foundId = true;
			}
		} else if (selectedOdooModel === "x_roofing") {
			if (clockingInfo.roofing && clockingInfo.roofing[0] === odooId) {
				foundId = true;
			}
		} else if (selectedOdooModel === "helpdesk.ticket") {
			if (clockingInfo.tickets && clockingInfo.tickets.includes(odooId)) {
				foundId = true;
			}
		} else if (selectedOdooModel === "x_krummy_capture") {
			if (clockingInfo.krummy && clockingInfo.krummy[0] === odooId) {
				foundId = true;
			}
		}
		setClockedIntoThis(foundId);
	}, [clockingData, odooId, selectedOdooModel]);
	useEffect(() => {
		if (!api || !token) {
			return;
		}
		const controller = new AbortController();
		const signal = controller.signal;

		setIsLoading(true);
		fetch(api + "/project/" + projectId, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			signal,
		})
			.then((res) => {
				if (!res.ok) {
					setIsLoading(false);
					// put an error here
				} else {
					return res.json();
				}
			})
			.then((res) => {
				if (res) {
					if (res.geoRecords && res.geoRecords.length > 0) {
						let geoData = res.geoRecords[0];
						setProjectLocation({ lat: geoData["x_approximate_latitude"], lng: geoData["x_approximate_longitude"] });
						setHasGeoFencing(geoData["x_studio_has_geofencing"]);
					} else {
						setProjectLocation(null);
						setHasGeoFencing(false);
					}
					setPublicEnabledTemplate(res.project[0].public_enabled);
					setClockedIntoThis(res.clockedIntoThis);
					setTemplateTypeId(res.templateType[0].id);
					if (res.project[0].contract_type.toLowerCase() === "residential") {
						setContractType("residential");
					} else if (res.project[0].contract_type.toLowerCase() === "commercial") {
						setContractType("commercial");
					} else {
						setContractType("all");
					}
					setIsLoading(false);
					let unsortedSections = res.sections;
					let sortedSections = unsortedSections.sort((a, b) => a.position - b.position);
					document.title = res.project[0].name;

					let tempRequiredFieldsBySection = {};
					let tempAerialMarkersMissingBySection = {};
					for (let i = 0; i < sortedSections.length; i++) {
						tempRequiredFieldsBySection[sortedSections[i].id] = [];
						tempAerialMarkersMissingBySection[sortedSections[i].id] = [];
					}

					setRequiredFieldsBySection(tempRequiredFieldsBySection);
					setAerialMarkersMissingBySection(tempAerialMarkersMissingBySection);

					if (res.project[0].stage === "assigned") {
						setShowStagePrompt(true);
					}

					setAllInitialThumbnailPathes(res.dbxImages);

					if (isGuest) {
						if (isApproved) {
							setStage("completed");
						} else {
							setStage("in-progress");
						}
					} else {
						setStage(res.project[0].stage);
					}
					if (res.solarGradeSiteVisitId && res.solarGradeProjectId) {
						setSolarGradeIds({
							visitId: res.solarGradeSiteVisitId,
							projectId: res.solarGradeProjectId,
						});
					}
					setKeyedSolarGradeData(res.keyedSolarGradeData || {});
					setKeyedKrummySolarGradeData(res.keyedKrummySolarGradeData || {});
					setProjectName(res.project[0].name);
					setOdooId(res.project[0].odoo_id);
					setSections(sortedSections);
					setTemplateName(res.project[0].template_name);
					setTemplateVersion(res.project[0].version);
					setTemplateId(res.project[0].template_id);
					setLastUpdatedBy(res.project[0].last_updated_by);
					setLastModifiedOn(res.project[0].last_modified_on);
					setTemplateStatus(res.project[0].template_status);
					setSelectedOdooModel(res.project[0].odoo_model);
					setOdooInstallationName(res.project[0].odoo_installation_name);
					setOdooParentName(res.project[0].odoo_parent_name);
					setTemplateNavMappings(res.navMappings);
					setHelpdeskStage(res.helpdeskStage);
					setParentRecord(res.parentRecord);
					setChildRecords(res.childRecords);
					setInstallationRecord(res.installationRecord);
					setDropboxLinkPath(res.dropboxLinkPath);
					setTickets(res.tickets);

					if (res.pendingImages) {
						setStartingPendingImages(res.pendingImages);
					}

					if (ppc) {
						const preCompleteTicket = res.tickets.find((ticket) => {
							let teamIsProjectComplete = false;
							if (Array.isArray(ticket.team_id) && ticket.team_id.length > 1) {
								const teamName = ticket.team_id[1];
								teamIsProjectComplete = teamName.toLowerCase() === "Pre-Project Complete".toLowerCase();
							}

							let stageIsScheduled = false;
							if (Array.isArray(ticket.stage_id) && ticket.stage_id.length > 1) {
								const stageName = ticket.stage_id[1];
								stageIsScheduled = stageName.toLowerCase() === "Scheduled".toLowerCase();
							}

							let foundId = ticket.id.toString() === ppc.toString();

							return teamIsProjectComplete && stageIsScheduled && foundId;
						});

						setHasPreProjectCompleteTicket(preCompleteTicket ? true : false);
					}
					let tempPortfolioType = "all";
					let projectPortfolioType = res.project[0].portfolio_type.toLowerCase();
					if (projectPortfolioType === "public storage") {
						tempPortfolioType = "ps";
					} else if (projectPortfolioType === "extra space storage") {
						tempPortfolioType = "ess";
					} else if (projectPortfolioType !== "") {
						tempPortfolioType = "misc";
					}
					setPortfolioType(tempPortfolioType);
					let gmapLink = res.project[0].google_map_link;
					if (gmapLink) {
						let splitLink = gmapLink.split("href");
						if (splitLink.length === 2) {
							gmapLink = `${splitLink[0]} rel="noopener noreferrer" target='_blank' href${splitLink[1]}`;
						} else if (splitLink.length >= 3) {
							gmapLink = `${splitLink[0]} rel="noopener noreferrer" target='_blank' href${splitLink.slice().join("href")}`;
						}
					}
					setGoogleMapLink(gmapLink);

					let conditionsArray = [];
					for (let i = 0; i < sortedSections.length; i++) {
						if (sortedSections[i].section_condition_id !== 0) {
							conditionsArray.push({
								id_of_conditioned: sortedSections[i].id,
								conditionMet: false,
								condition_type: sortedSections[i].condition_type,
								condition_value: sortedSections[i].condition_value,
								field_condition_id: sortedSections[i].field_condition_id,
								section_condition_id: sortedSections[i].section_condition_id,
							});
						}
					}
					setConditionalData(conditionsArray);
				}
			})
			.catch((err) => {});

		return () => {
			controller.abort();
		};
	}, [api, token, projectId, navigate, ppc, isGuest, isApproved]);

	const loadAerialData = useCallback(
		async (signal) => {
			if (!api || !token || !projectId || isGuest) {
				return false;
			}
			setAerialLoading(true);
			setAerialLoadError("");
			try {
				const aerialResponse = await fetch(`${api}/project/${projectId}/aerial-view`, {
					method: "GET",
					headers: {
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
					signal,
				});
				const responseBody = await aerialResponse.json();
				if (!aerialResponse.ok) {
					throw new Error(responseBody.message || "Unable to load the aerial view.");
				}
				setAerialData({
					...responseBody,
					sections: (responseBody.sections || []).map((section) => ({
						...section,
						fields: section.fields.map((field) => ({
							...field,
							...(aerialFieldOverridesReference.current[field.id] || {}),
						})),
					})),
				});
				setSelectedAerialDropboxId((currentId) => {
					const availableImages = Array.isArray(responseBody.images) && responseBody.images.length > 0 ? responseBody.images : responseBody.image ? [responseBody.image] : [];
					if (currentId && availableImages.some((image) => image.dropboxId === currentId)) {
						return currentId;
					}
					return responseBody.image?.dropboxId || availableImages[0]?.dropboxId || null;
				});
				setAerialSyncEpoch((currentEpoch) => currentEpoch + 1);
				return true;
			} catch (error) {
				if (error.name !== "AbortError") {
					setAerialLoadError(error.message || "Unable to load the aerial view.");
				}
				return false;
			} finally {
				if (!signal?.aborted) {
					setAerialLoading(false);
				}
			}
		},
		[api, token, projectId, isGuest],
	);

	useEffect(() => {
		aerialFieldOverridesReference.current = {};
		setAerialData(null);
		setSelectedAerialDropboxId(null);
		setAerialLoadError("");
		setFocusedFieldId(null);
		setAerialFocusRequestId(0);
		setAerialSyncEpoch(0);
	}, [projectId]);

	useEffect(() => {
		const controller = new AbortController();
		loadAerialData(controller.signal);
		return () => controller.abort();
	}, [loadAerialData]);

	const handleAerialImageRequestCreated = useCallback((imageRequest) => {
		setAerialData((currentData) => {
			if (!currentData || !imageRequest) {
				return currentData;
			}
			const currentRequests = currentData.imageRequests || [];
			if (currentRequests.some((existingRequest) => existingRequest.id === imageRequest.id)) {
				return currentData;
			}
			return {
				...currentData,
				imageRequests: [...currentRequests, imageRequest],
			};
		});
	}, []);

	const applyAerialMarkerSnapshot = useCallback(({ markers, aerialViewVersion }) => {
		setAerialData((currentData) => {
			if (!currentData) {
				return currentData;
			}
			const nextData = {
				...currentData,
				markers: Array.isArray(markers) ? markers : currentData.markers,
			};
			if (Number.isInteger(Number(aerialViewVersion))) {
				nextData.aerialViewVersion = Number(aerialViewVersion);
			}
			return nextData;
		});
	}, []);

	const handleAerialMarkerRemove = useCallback((fieldId) => {
		setAerialData((currentData) => {
			if (!currentData) {
				return currentData;
			}
			return {
				...currentData,
				markers: (currentData.markers || []).filter((marker) => marker.fieldId !== fieldId),
			};
		});
	}, []);

	const handleAerialMarkerChange = useCallback((changedMarker) => {
		if (changedMarker.projectFieldId) {
			aerialFieldOverridesReference.current[changedMarker.fieldId] = {
				...(aerialFieldOverridesReference.current[changedMarker.fieldId] || {}),
				projectFieldId: changedMarker.projectFieldId,
			};
		}
		setAerialData((currentData) => {
			if (!currentData) {
				return currentData;
			}
			const currentMarkers = currentData.markers || [];
			const markerExists = currentMarkers.some((marker) => marker.fieldId === changedMarker.fieldId);
			return {
				...currentData,
				markers: markerExists
					? currentMarkers.map((marker) => (marker.fieldId === changedMarker.fieldId ? { ...marker, ...changedMarker } : marker))
					: [...currentMarkers, changedMarker],
				sections: changedMarker.projectFieldId
					? (currentData.sections || []).map((section) => ({
							...section,
							fields: section.fields.map((field) =>
								field.id === changedMarker.fieldId ? { ...field, projectFieldId: changedMarker.projectFieldId } : field,
							),
						}))
					: currentData.sections,
			};
		});
	}, []);

	const handleAerialFieldDataChange = useCallback((fieldId, changedValues) => {
		aerialFieldOverridesReference.current[fieldId] = {
			...(aerialFieldOverridesReference.current[fieldId] || {}),
			...changedValues,
		};
		setAerialData((currentData) => {
			if (!currentData?.sections) {
				return currentData;
			}
			return {
				...currentData,
				sections: currentData.sections.map((section) => ({
					...section,
					fields: section.fields.map((field) => (field.id === fieldId ? { ...field, ...changedValues } : field)),
				})),
			};
		});
	}, []);

	const showAerialField = useCallback((fieldId) => {
		setFocusedFieldId(fieldId);
		setAerialFocusRequestId((currentRequestId) => currentRequestId + 1);
		setActiveTab("aerial");
	}, []);

	const showProjectField = useCallback((fieldId) => {
		setFocusedFieldId(fieldId);
		setActiveTab("project");

		const scrollToField = (remainingAttempts) => {
			const fieldElement = document.getElementById(`field-${fieldId}`);
			if (fieldElement) {
				fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
			} else if (remainingAttempts > 0) {
				window.setTimeout(() => scrollToField(remainingAttempts - 1), 100);
			}
		};
		window.setTimeout(() => scrollToField(20), 0);
	}, []);

	useEffect(() => {
		if (!api || !token) {
			return;
		}

		if (isGuest) {
			return;
		}
		const controller = new AbortController();
		const signal = controller.signal;

		const currentDate = new Date();

		const year = currentDate.getFullYear();
		const month = String(currentDate.getMonth() + 1).padStart(2, "0");
		const day = String(currentDate.getDate()).padStart(2, "0");
		const formattedDate = `${year}-${month}-${day}`;
		setIsLoading(true);
		fetch(api + "/project/clocking?date=" + formattedDate, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			signal,
		})
			.then((res) => {
				if (!res.ok) {
				} else {
					return res.json();
				}
			})
			.then((res) => {
				if (res) {
					setClockedIntoThis(res.clockedIntoThis);
					setClockingData({
						clockingInfo: res.clockingInfo,
						specialCode: res.clockingSpecialCode,
						lastClocked: res.lastClocked ? new Date(res.lastClocked) : null,
					});
				}
			})
			.catch((err) => {});

		return () => {
			controller.abort();
		};
	}, [api, isGuest, token]);

	const changeStage = (newStage, hasDownload, downloadName) => {
		if (isGuest) {
			return;
		}
		fetch(api + "/project/stage", {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			body: JSON.stringify({ id: projectId, stage: newStage, odoo_id: odooId }),
		}).then((res) => {
			if (res.ok) {
				setStage(newStage);
				setShowStagePrompt(false);
				if (hasDownload) {
					FileSaver.saveAs(segmentImage, downloadName);
				}
				setLastModifiedOn(new Date());
			}
		});
	};

	const openExportMenu = (e) => {
		if (isGuest) {
			return;
		}
		e.preventDefault();
		setShowExportMenu(true);
	};

	const updateTemplateToLatestVersion = (e) => {
		if (isGuest) {
			return;
		}
		e.preventDefault();
		if (updatingTemplateVersion) {
			return;
		}

		setUpdatingTemplateVersion(true);
		fetch(api + "/project/update-template/" + projectId, {
			method: "post",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
		}).then((res) => {
			setUpdatingTemplateVersion(false);
			if (res.ok) {
				window.location.reload(true);
			} else {
			}
		});
	};

	const moveToSIQ = (ticketOdooId) => {
		if (isGuest) {
			return;
		}
		setMovingToSIQ(true);
		fetch(api + "/projectdata/siq/" + ticketOdooId, {
			method: "put",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
		}).then((res) => {
			if (res.ok) {
				setMovingToSIQ(false);
				if (hasPreProjectCompleteTicket && ticketOdooId === ppc) {
					setHasPreProjectCompleteTicket(false);
				} else {
					setHelpdeskStage("SIQ Review");
				}
			}
		});
	};

	const styleByItemCount = () => {
		return "grid grid-cols-2 md:grid-rows-2 grid-rows-auto";
		// let count = 1;
		// if (props.reportAccess || props.isAdmin) {
		// 	count++;
		// }
		// if (selectedOdooModel === "helpdesk.ticket" && helpdeskStage !== "SIQ Review") {
		// 	count++;
		// }
		// if (odooId && (props.templateAccess || props.isAdmin)) {
		// 	count++;
		// }
		// if (props.exportAccess || props.isAdmin) {
		// 	count++;
		// }
		// if (props.isAdmin && templateStatus !== 2) {
		// 	count++;
		// }
		// if (count === 1) {
		// 	return "";
		// } else if (count <= 4) {
		// 	return "grid grid-cols-2 md:grid-rows-2 grid-rows-auto";
		// } else {
		// 	return "grid grid-cols-2 md:grid-rows-3 grid-rows-auto";
		// }
	};

	useEffect(() => {
		if (openChatLog) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-hidden");
		}

		return () => {
			document.body.classList.remove("overflow-hidden");
		};
	}, [openChatLog]);

	useEffect(() => {
		if (showStagePrompt && stage === "assigned") {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-hidden");
		}

		return () => {
			document.body.classList.remove("overflow-hidden");
		};
	}, [showStagePrompt, stage]);

	const hasAerialTaskMapping =
		!isGuest && (selectedOdooModel === "project.task" || (Array.isArray(installationRecord) && installationRecord.length > 0));
	const showAerialTab = hasAerialTaskMapping && aerialData?.available !== false;

	useEffect(() => {
		if (activeTab === "aerial" && !showAerialTab && !aerialLoading) {
			setActiveTab("project");
		}
	}, [activeTab, aerialLoading, showAerialTab]);

	return (
		<div className="">
			{projectName && !isGuest && templateStatus !== 2 ? <OldTemplateBanner /> : null}
			{projectName && isGuest && user ? (
				<div className="p-2 font-bold text-center bg-green-300 text-black z-50 w-full">You are viewing this project as a guest.</div>
			) : null}

			{clockBannerToShow === "warning" ? <NearingLateClockoutBanner /> : null}

			{clockBannerToShow === "over" ? <LateClockOutBanner /> : null}
			<PermissionGuard permission={PermissionsMap.PROJECT_CHAT}>
				<>
					{selectedOdooModel !== "helpdesk.ticket" ? null : openChatLog ? null : (
						<div className="relative z-[150]">
							<div className="fixed bottom-5 right-5 ">
								<button onClick={() => setOpenChatLog(true)} className="bg-black p-2 text-white rounded-full">
									<IoChatbubbleEllipsesSharp size={24} />
								</button>
							</div>
						</div>
					)}
				</>
			</PermissionGuard>
			{showInvalidDropboxModal ? (
				<div
					className="text-black fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black/50 z-50"
					onClick={() => setShowInvalidDropboxModal(false)}>
					<div className="bg-white w-full lg:w-1/4 m-2 rounded-lg " onClick={(e) => e.stopPropagation()}>
						<div className="m-4">
							<p className="text-3xl font-bold text-center">Invalid Dropbox Link</p>
						</div>
						<div className="px-4 pb-4">
							<p>This project has not been properly setup in Dropbox. Please contact IT and ask them to migrate the folder.</p>
							<div className="flex justify-center w-full mt-4">
								<button type="button" className="rounded-lg bg-gray-500 p-2 text-white " onClick={() => setShowInvalidDropboxModal(false)}>
									Close
								</button>
							</div>
						</div>
					</div>
				</div>
			) : null}
			<ScrollToTop
				smooth
				top={500}
				width="1"
				height="1"
				style={{ background: (76, 175, 80, 0.3), right: 70, bottom: 20 }}
				component={<BsArrowUpCircleFill className="w-full h-full" />}
				className={`bg-opacity-0 #{!important} ${activeTab !== "project" ? "hidden" : ""}`}
			/>
			{/*<ScrollButton />*/}
			{updatingTemplateVersion ? (
				<div className="text-black fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black/50 z-50">
					<div className="bg-white flex flex-col items-center justify-center p-4 rounded-lg ">
						<LoadingCircle />
						<p>Updating template version...</p>
					</div>
				</div>
			) : null}
			{showExportMenu ? (
				<PermissionGuard permission={PermissionsMap.EXPORT_BUTTON}>
					<ExportModal
						setShowExportMenu={setShowExportMenu}
						projectid={projectId}
						api={api}
						token={token}
						projectName={projectName}
						templateName={templateName}
					/>
				</PermissionGuard>
			) : null}

			{showStagePrompt && stage === "assigned" ? (
				<StagePrompt
					prompt={"This is a new project"}
					yesText="Start project"
					noText="Go Back"
					confirmAction={() => {
						changeStage("in-progress", true, "pms-start-segment-" + projectName);
					}}
					cancelAction={() => {
						navigate(-1);
					}}
					hasDownloadSegment={true}
					downloadName={"pms-start-segment-" + projectName}
				/>
			) : null}
			<PermissionGuard permission={PermissionsMap.CAN_CHANGE_STAGES_BUTTON}>
				<>
					{showStagePrompt && stage === "in-progress" ? (
						<StagePrompt
							prompt={"Move to finalize?"}
							yesText="Finalize Project"
							noText="Cancel"
							confirmAction={() => {
								changeStage("finalize");
							}}
							cancelAction={() => {
								setShowStagePrompt(false);
							}}
						/>
					) : null}

					{showStagePrompt && stage === "finalize" ? (
						<StagePrompt
							prompt={"Move to completed?"}
							yesText="Complete Project"
							noText="Cancel"
							confirmAction={() => {
								changeStage("completed", true, "pms-end-segment-" + projectName);
							}}
							cancelAction={() => {
								setShowStagePrompt(false);
							}}
							hasDownloadSegment={true}
							downloadName={"pms-end-segment-" + projectName}
						/>
					) : null}
					{showStagePrompt && stage === "completed" ? (
						<StagePrompt
							prompt={"Move to finalize?"}
							yesText="Back to finalize"
							noText="Cancel"
							confirmAction={() => {
								changeStage("finalize");
							}}
							cancelAction={() => {
								setShowStagePrompt(false);
							}}
						/>
					) : null}
				</>
			</PermissionGuard>

			<PermissionGuard permission={PermissionsMap.PROJECT_CHAT}>
				<MessageLogs
					api={api}
					token={token}
					odoo_model={selectedOdooModel}
					odoo_id={odooId}
					openChatLog={openChatLog}
					setOpenChatLog={setOpenChatLog}
				/>
			</PermissionGuard>

			<div className="p-5 bg-gray-100 flex flex-col lg:flex-row justify-between gap-4 w-full relative">
				<div>
					<p className="text-2xl font-bold">Project: {projectName}</p>
					<p className="text-xl ">Template: {templateName + " - v" + templateVersion} </p>
					<p className="text-lg ">Stage: {stage}</p>
					<div className="flex gap-2 text-lg">
						<p>Location: </p>
						{googleMapLink === "" || googleMapLink === "failedtogetdataforpmscode400" ? (
							<p>-</p>
						) : (
							<div className="text-blue-400 hover:text-blue-600 underline" dangerouslySetInnerHTML={{ __html: googleMapLink }}></div>
						)}
					</div>
					{lastUpdatedBy ? (
						<div className="flex gap-2 text-lg">
							<p>Last updated by: {`${lastUpdatedBy} ${lastModifiedOn ? `@ ${new Date(lastModifiedOn).toLocaleString("en-US")}` : ""}`}</p>
						</div>
					) : null}
					<PermissionGuard permission={PermissionsMap.DROPBOX_ACCESS}>
						<>
							{isLoading ? null : dropboxLinkPath ? (
								<div className="flex gap-2 items-center  ">
									<FaDropbox size={20} color="#0061FE" />
									<Link to={dropboxLinkPath} className="text-blue-400 hover:text-blue-600 underline">
										Dropbox Access
									</Link>
									<p></p>
								</div>
							) : (
								<div className="flex gap-2 items-center text-blue-400 hover:text-blue-600 underline ">
									<button
										className="flex gap-2 items-center"
										onClick={() => {
											setShowInvalidDropboxModal(true);
										}}>
										<FaDropbox size={20} color="#0061FE" />
										<p>Dropbox Access</p>
									</button>
								</div>
							)}
						</>
					</PermissionGuard>
				</div>
				<div>
					<div className="mb-1">
						{isLoading || isGuest ? null : (
							<>
								<ClockOutButton
									api={api}
									token={token}
									projectsRoute={""}
									odooId={odooId}
									odooModel={selectedOdooModel}
									clockingData={clockingData}
									setClockingData={setClockingData}
									isFullWidth={true}
									clockedIntoThis={clockedIntoThis}
									showClock={true}
									clockBannerToShow={clockBannerToShow}
									setClockBannerToShow={setClockBannerToShow}
									projectLocation={projectLocation}
									hasGeofencing={hasGeoFencing}
								/>
								<ClockInButton
									api={api}
									token={token}
									projectsRoute={""}
									odooId={odooId}
									odooModel={selectedOdooModel}
									clockingData={clockingData}
									setClockingData={setClockingData}
									isFullWidth={true}
									clockedIntoThis={clockedIntoThis}
									showClock={true}
									clockBannerToShow={clockBannerToShow}
									setClockBannerToShow={setClockBannerToShow}
									projectLocation={projectLocation}
									hasGeofencing={hasGeoFencing}
									hasScheduleCheck={true}
									krummyProjectId={projectId}
								/>
							</>
						)}
					</div>

					{isLoading ? null : isGuest && stage !== "completed" ? (
						templateName !== "Design Approval" ? null : (
							<div className="text-black w-full flex flex-col gap-2">
								{localIsRejected ? (
									<>
										<div className="text-black text-center w-full border border-black p-2 rounded-md bg-white">
											<p>The design has been rejected, and the 1st Light team has been notified</p>
											{localRejectedBy || localRejectedAt ? (
												<p className="mt-2 text-sm">
													Rejected by {localRejectedBy || "Unknown"}
													{localRejectedAt ? ` @ ${formatRejectedAt(localRejectedAt)}` : ""}
												</p>
											) : null}
											{/* {localRejectedReason ? (
												<button
													type="button"
													onClick={() => setShowRejectNote(true)}
													className="mt-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm">
													View note
												</button>
											) : null} */}
										</div>
										{showRejectNote ? (
											<div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-50 z-[2000] flex justify-center items-center">
												<div className="bg-white text-black rounded-lg shadow-xl w-full md:w-2/5">
													<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
														<p className="text-xl font-bold">Rejection Note</p>
														<button
															type="button"
															onClick={() => setShowRejectNote(false)}
															className="text-gray-600 hover:text-gray-800 px-2 py-1 rounded-md">
															Close
														</button>
													</div>
													<div className="px-6 py-5">
														<div className="border border-gray-200 bg-gray-50 rounded-md p-3">
															<p className="text-base whitespace-pre-wrap break-words max-h-64 overflow-y-auto">{localRejectedReason}</p>
														</div>
													</div>
													<div className="px-6 pb-5">
														<button
															type="button"
															onClick={() => setShowRejectNote(false)}
															className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md w-full">
															Done
														</button>
													</div>
												</div>
											</div>
										) : null}
									</>
								) : (
									<>
										<ApproveDesignButton api={api} projectId={projectId} tempToken={token} setStage={setStage} />
										<RejectDesignButton
											api={api}
											projectId={projectId}
											tempToken={token}
											validateReject={validateReject}
											onRejected={({ rejectedBy: name, rejectedReason: reason, rejectedAt: at }) => {
												setLocalIsRejected(true);
												setLocalRejectedBy(name || "");
												setLocalRejectedReason(reason || "");
												setLocalRejectedAt(at || null);
											}}
										/>
									</>
								)}
							</div>
						)
					) : isGuest && stage === "completed" ? (
						<div className="text-black text-center w-full border border-black  p-2 rounded-md bg-white">
							<p>Design has been approved</p>
							<p>You can no longer edit data</p>
						</div>
					) : null}
					{isGuest ? null : (
						<div className={`gap-1 bg-gray-100 text-white select-none ${styleByItemCount()}`}>
							<PermissionGuard permission={PermissionsMap.CLEAR_RECORD_BUTTON}>
								<ClearRecordButton projectName={projectName} projectid={projectId} api={api} token={token} />
							</PermissionGuard>
							{/* {props.isAdmin && props.isAdmin === true && parseInt(props.userId) === DELETE_ID ? (
								<DeleteProjectButton projectName={projectName} deleteThisProject={deleteThisProject} />
							) : null}{" "} */}
							<PermissionGuard permission={PermissionsMap.REPORT_CREATION}>
								<ReportButton
									api={api}
									token={token}
									odooId={odooId}
									selectedOdooModel={selectedOdooModel}
									templateNavMappings={templateNavMappings}
									parentRecord={parentRecord}
									childrenRecords={childRecords}
									tickets={tickets}
									projectName={projectName}
									templateName={templateName}
								/>
							</PermissionGuard>
							<PermissionGuard permission={PermissionsMap.CAN_CHANGE_STAGES_BUTTON}>
								{stage && stage !== "assigned" ? (
									<div
										className="flex items-center gap-1 cursor-pointer bg-blue-600 hover:bg-blue-700 p-2 rounded-lg w-full select-none"
										onClick={() => setShowStagePrompt(true)}>
										{stage !== "completed" ? <ImArrowRight /> : <ImArrowLeft />}
										<p>
											{stage === "assigned" ? "Move to in-progress" : null}
											{stage === "in-progress" ? "Move to finalize" : null}
											{stage === "finalize" ? "Move to complete" : null}
											{stage === "completed" ? "Move to finalize" : null}
										</p>
									</div>
								) : null}
							</PermissionGuard>

							<PermissionGuard permission={PermissionsMap.MOVE_TO_SIQ_BUTTON}>
								<>
									{hasPreProjectCompleteTicket && selectedOdooModel !== "helpdesk.ticket" ? (
										<div
											className={`flex items-center gap-1 cursor-pointer ${
												movingToSIQ ? "bg-gray-700 hover:bg-gray-800" : "bg-blue-600 hover:bg-blue-700"
											} p-2 rounded-lg w-full select-none`}
											onClick={() => {
												if (!movingToSIQ) {
													moveToSIQ(ppc);
												}
											}}>
											{movingToSIQ ? (
												<>
													<LoadingCircle />
													<p>Moving to SIQ...</p>
												</>
											) : (
												<>
													<ImArrowRight />
													<p>Move to SIQ</p>
												</>
											)}
										</div>
									) : null}
									{selectedOdooModel === "helpdesk.ticket" && helpdeskStage === "Scheduled" ? (
										<div
											className={`flex items-center gap-1 cursor-pointer ${
												movingToSIQ ? "bg-gray-700 hover:bg-gray-800" : "bg-blue-600 hover:bg-blue-700"
											} p-2 rounded-lg w-full select-none`}
											onClick={() => {
												if (!movingToSIQ) {
													moveToSIQ(odooId);
												}
											}}>
											{movingToSIQ ? (
												<>
													<LoadingCircle />
													<p>Moving to SIQ...</p>
												</>
											) : (
												<>
													<ImArrowRight />
													<p>Move to SIQ</p>
												</>
											)}
										</div>
									) : null}
								</>
							</PermissionGuard>

							<PermissionGuard permission={PermissionsMap.TEMPLATES_READ}>
								{odooId ? (
									<div className="flex items-center cursor-pointer ">
										<Link
											to={
												"/odoo-project/" +
												odooId +
												"/template-select?model=" +
												selectedOdooModel +
												`&contract=${contractType}&portfolio=${portfolioType}`
											}
											className="w-full select-none h-full">
											<div className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 p-2 rounded-lg select-none h-full">
												<BsArrowLeftRight />
												<p className="">Switch Template</p>
											</div>
										</Link>
									</div>
								) : null}
							</PermissionGuard>

							{(isGuest && shopEnabled === 1) || userPermissions.includes(PermissionsMap.ODOO_ORDERS) ? (
								<>
									{odooId && selectedOdooModel && projectName ? (
										<ShopButton
											api={api}
											token={isGuest ? searchParams.get("token") : token}
											odooId={odooId}
											odooModel={selectedOdooModel}
											projectName={projectName}
											isGuest={isGuest}
											projectId={projectId}
											guestToken={guestToken}
										/>
									) : null}
								</>
							) : null}

							<PermissionGuard permission={PermissionsMap.EXPORT_BUTTON}>
								<div
									className="flex items-center gap-1  bg-blue-600 hover:bg-blue-700 p-2 rounded-lg w-full cursor-pointer select-none"
									onClick={(e) => {
										openExportMenu(e);
									}}>
									<AiOutlineExport />
									<p>Export</p>
								</div>
							</PermissionGuard>
							{solarGradeIds && solarGradeIds.visitId && solarGradeIds.projectId ? (
								<SolarGradeUpdateButton projectId={projectId} solarGradeIds={solarGradeIds} />
							) : null}

							<PermissionGuard permission={PermissionsMap.UPDATE_TEMPLATE_BUTTON}>
								<>{templateStatus !== 2 ? <UpdateTemplateButton updateTemplateToLatestVersion={updateTemplateToLatestVersion} /> : null}</>
							</PermissionGuard>
							{publicEnabledTemplate ? (
								<PermissionGuard permission={PermissionsMap.PUBLIC_LINK}>
									<GeneratePublicLinkButton projectId={projectId} />
								</PermissionGuard>
							) : null}
						</div>
					)}
				</div>
			</div>
			{/* Tab Navigation */}
			{odooId && !isGuest ? (
				<>
					<div className="border-b border-gray-300 bg-gray-700">
						<div className="flex">
							<button
								onClick={() => setActiveTab("project")}
								className={`px-6 py-3 font-semibold transition-colors ${
									activeTab === "project"
										? "border-b-2 border-blue-600 text-white bg-gray-600"
										: "text-gray-200 hover:text-white hover:bg-gray-600"
								}`}>
								Project
							</button>
							<button
								onClick={() => setActiveTab("chat")}
								className={`px-6 py-3 font-semibold transition-colors ${
									activeTab === "chat"
										? "border-b-2 border-blue-600 text-white bg-gray-600"
										: "text-gray-200 hover:text-white hover:bg-gray-600"
								}`}>
								Logs
							</button>
							{showAerialTab ? (
								<button
									onClick={() => setActiveTab("aerial")}
									className={`px-6 py-3 font-semibold transition-colors ${
										activeTab === "aerial"
											? "border-b-2 border-blue-600 text-white bg-gray-600"
											: "text-gray-200 hover:text-white hover:bg-gray-600"
									}`}>
									Aerial View
								</button>
							) : null}
						</div>
					</div>
				</>
			) : null}

			{/* Tab Content */}
			{/* Project View - Hidden when chat tab is active */}
			<div className={activeTab === "project" ? "" : "hidden"}>
				<div className="flex flex-col md:flex-row ">
					<div className="basis-1/4">
						<div className="md:sticky md:top-0  ">
							<PermissionGuard permission={PermissionsMap.SHOW_PROJECT_NAV}>
								<NavigationArea
									api={api}
									token={token}
									templateNavMappings={templateNavMappings}
									selectedOdooModel={selectedOdooModel}
									odooId={odooId}
									contractType={contractType}
									portfolioType={portfolioType}
									parentRecord={parentRecord}
									childRecords={childRecords}
									tickets={tickets}
									installationRecord={installationRecord}
									isLoading={isLoading}
								/>
							</PermissionGuard>
							<div className="flex justify-between items-center bg-gray-200 px-4 py-1">
								<p className="font-bold text-xl">Section List</p>
							</div>
							<div className="max-h-[60vh] overflow-y-scroll  px-4">
								{sections && sections.length > 0 ? (
									sections.map((value) => {
										for (let i = 0; i < conditionalData.length; i++) {
											if (conditionalData[i].id_of_conditioned === value.id && conditionalData[i].conditionMet === false) {
												return null;
											}
										}

										if (
											solarGradeIds &&
											solarGradeIds.visitId &&
											solarGradeIds.projectId &&
											!solarGradeFilteredSections.includes(value.id)
										) {
											return null;
										}

										return (
											<div key={value.id} className="border-l-4 p-1 pl-2 flex justify-between items-center">
												<a className="text-blue-500 hover:text-blue-700 underline" href={"#" + value.name.replaceAll(" ", "-")}>
													{`${value.name}`}
												</a>

												{showElement ? (
													<div className="flex items-center gap-1">
														{requiredFieldsBySection[value.id]?.length > 0 ? (
															<div
																className="flex items-center text-sm bg-yellow-300 p-1 rounded-lg gap-1 opacity-0"
																style={{ opacity: showElement ? 1 : 0 }}>
																<AiFillWarning />
																<p>{requiredFieldsBySection[value.id].length}</p>
															</div>
														) : null}
														{aerialMarkersMissingBySection[value.id]?.length > 0 ? (
															<div
																className="flex items-center text-sm bg-orange-400 p-1 rounded-lg gap-1 opacity-0"
																style={{ opacity: showElement ? 1 : 0 }}>
																<FaMapMarkerAlt />
																<p>{aerialMarkersMissingBySection[value.id].length}</p>
															</div>
														) : null}
													</div>
												) : null}
											</div>
										);
									})
								) : isLoading ? (
									<div className="flex flex-col items-center justify-center w-full my-2">
										<LoadingCircle />
										<p>Loading Sections...</p>
									</div>
								) : (
									<p>This is an empty template</p>
								)}
							</div>
						</div>
					</div>

					<div className="basis-3/4 p-2  bg-gray-200" id="project-data">
						{sections && sections.length > 0 ? (
							sections.map((value, index) => {
								let showThisSection = true;
								for (let i = 0; i < conditionalData.length; i++) {
									if (conditionalData[i].id_of_conditioned === value.id && conditionalData[i].conditionMet === false) {
										showThisSection = false;
										break;
									}
								}

								return (
									<ProjectSection
										conditionalData={conditionalData}
										sectionId={value.id}
										setConditionalData={setConditionalData}
										templateName={templateName}
										templateId={templateId}
										projectName={projectName}
										key={value.id}
										projectId={projectId}
										api={api}
										data={value}
										token={token}
										stage={stage}
										requestForm={false}
										templateTypeId={templateTypeId}
										projectHtmlId={"section-" + index}
										requiredFieldsBySection={requiredFieldsBySection}
										setRequiredFieldsBySection={setRequiredFieldsBySection}
										aerialMarkersMissingBySection={aerialMarkersMissingBySection}
										setAerialMarkersMissingBySection={setAerialMarkersMissingBySection}
										aerialMarkersReady={isGuest || aerialData !== null || Boolean(aerialLoadError)}
										showThisSection={showThisSection}
										setLastModifiedOn={setLastModifiedOn}
										initialThumbnailPathes={allInitialThumbnailPathes.filter((filterData) =>
											filterData.name.includes(value.name.replaceAll("/", "-") + " - ")
										)}
										showElement={showElement}
										selectedOdooModel={selectedOdooModel}
										setLastUpdatedBy={setLastUpdatedBy}
										odooInstallationName={odooInstallationName}
										odooParentName={odooParentName}
										isGuest={isGuest}
										keyedSolarGradeData={keyedSolarGradeData}
										solarGradeIds={solarGradeIds}
										keyedKrummySolarGradeData={keyedKrummySolarGradeData}
										setSolarGradeFilteredSections={setSolarGradeFilteredSections}
										startingPendingImages={startingPendingImages}
										focusedFieldId={focusedFieldId}
										hasAerialView={showAerialTab && Boolean(aerialData?.available)}
										aerialMarkerFieldIds={(aerialData?.markers || []).map((marker) => marker.fieldId)}
										onShowOnAerial={showAerialField}
										onFieldDataChange={handleAerialFieldDataChange}
									/>
								);
							})
						) : isLoading ? (
							<div className="flex flex-col items-center justify-center w-full my-2">
								<LoadingCircle />
								<p>Loading Sections</p>
							</div>
						) : (
							<p>This is an empty template</p>
						)}
					</div>
				</div>
			</div>

			{/* Chat View - Hidden when project tab is active */}
			{odooId && !isGuest ? (
				<>
					<div className={activeTab === "chat" ? "" : "hidden"}>
						<RoomsSearchView mode="project" projectId={Number(projectId)} />
					</div>
				</>
			) : null}

			{showAerialTab ? (
				<div className={activeTab === "aerial" ? "" : "hidden"}>
					{aerialData?.available ? (
						<AerialView
							api={api}
							token={token}
							projectId={projectId}
							aerialData={aerialData}
							projectSections={sections}
							selectedAerialDropboxId={selectedAerialDropboxId}
							onSelectAerialImage={setSelectedAerialDropboxId}
							focusedFieldId={focusedFieldId}
							aerialFocusRequestId={aerialFocusRequestId}
							aerialSyncEpoch={aerialSyncEpoch}
							stage={stage}
							onMarkerChange={handleAerialMarkerChange}
							onMarkerRemove={handleAerialMarkerRemove}
							onFocusField={setFocusedFieldId}
							onViewProjectField={showProjectField}
							onApplyMarkerSnapshot={applyAerialMarkerSnapshot}
							onImageRequestCreated={handleAerialImageRequestCreated}
						/>
					) : aerialLoading ? (
						<div className="flex min-h-[60vh] flex-col items-center justify-center bg-gray-200">
							<LoadingCircle />
							<p>Loading aerial view...</p>
						</div>
					) : aerialLoadError ? (
						<div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 bg-gray-200 p-4 text-center">
							<p className="text-red-700">{aerialLoadError}</p>
							<button type="button" className="rounded bg-blue-600 px-4 py-2 text-white" onClick={() => loadAerialData()}>
								Retry
							</button>
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
};

export default Project;
